// src/components/Story/ByCategory.jsx
import React, { memo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import config from "../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Components
import Filter from "../Portfolio/Filter";
import { ImageOff } from 'lucide-react';

// Icônes SVG simples
const GridIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
    />
  </svg>
);

const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const ShoppingCartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const ByCategory = memo(({ filterData, loading, setLoading }) => {
  const [products, setProducts] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState("Chargement...");
  const [viewMode, setViewMode] = useState("grid"); // grid | list
  const location = useLocation();
  const categoryId = location.pathname.split("/").pop();

  const isGrid = viewMode === "grid";

  const containerClass = isGrid
    ? "grid grid-cols-1 min-sm:grid-cols-2 min-lg:grid-cols-3 min-xl:grid-cols-4 gap-6 min-md:gap-8"
    : "flex flex-col min-md:grid gap-5 min-md:grid-cols-2";

  const cardClass = isGrid
    ? "group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-2xl transition-all duration-500 flex flex-col h-full"
    : "group relative flex flex-row gap-6 bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 p-2 h-full";

  const imageWrapperClass = isGrid
    ? "relative aspect-[1.3] overflow-hidden rounded-t-2xl"
    : "relative w-48 min-sm:w-56 min-md:w-64 min-lg:w-72 min-xl:w-80 shrink-0 overflow-hidden rounded-2xl";

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${config.REACT_APP_API_URL}/allproductbyCategory/${categoryId}`,{
            headers: { 
            'x-api-key': config.SITE_API_KEY
           }}
        );
        const data = response.data?.result || [];
        setProducts(data);

        if (data.length > 0 && data[0].category?.name) {
          setCategoryTitle(data[0].category.name);
        } else {
          setCategoryTitle("Catégorie");
        }
      } catch (error) {
        console.error("Erreur récupération produits:", error);
        setProducts([]);
        setCategoryTitle("Erreur");
        toast.error("Impossible de charger les produits");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId, setLoading]);

  const handleAddToBasket = async (productId) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.warning("Vous devez être connecté pour ajouter au panier");
      return;
    }

    try {
      const response = await axios.post(
        `${config.REACT_APP_API_URL}/add-to-basket`,
        { productId: productId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Produit ajouté au panier !");
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de l'ajout");
    }
  };

  return (
        <>
        <Filter title={categoryTitle} filterData={filterData} />
    <section className="py-10 md:py-16 bg-gray-50/60">
      <div className="mx-auto px-5 min-xl:px-8 min-xl:w-4/5">
        <ToastContainer position="bottom-right" theme="light" autoClose={4000} />

        {/* En-tête + switch vue */}
        <div className="flex flex-col min-sm:flex-row justify-between items-start min-sm:items-center mb-10 md:mb-14 gap-6">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-gray-900 tracking-tight">
          </h2>

          <div className="flex items-center gap-2 bg-white rounded-full shadow-sm border border-gray-200 p-1.5">
            <button
              aria-label="Vue grille"
              onClick={() => setViewMode("grid")}
              className={`p-2.5 rounded-full transition-all ${
                isGrid ? "bg-gray-900 text-white shadow-md" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <GridIcon />
            </button>
            <button
              aria-label="Vue liste"
              onClick={() => setViewMode("list")}
              className={`p-2.5 rounded-full transition-all ${
                !isGrid ? "bg-gray-900 text-white shadow-md" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ListIcon />
            </button>
          </div>
        </div>

        {loading ? (
          <div className={containerClass}>
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-gray-200 rounded-2xl"
                style={{ aspectRatio: isGrid ? "4 / 5" : "auto", height: isGrid ? "auto" : "220px" }}
              />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 text-gray-500">
            <p className="text-xl font-medium">Aucun produit disponible dans cette catégorie</p>
          </div>
        ) : (
          <ul className={containerClass}>
            {products.map((item) => {
              const hasImage = item.picture && item.picture !== "/picture/product/";

              return (
                <li key={item._id}>
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  >
                                      <Link
                                        to={`/single-product/${item._id}`}
                                        className="block focus:outline-none focus:ring-2 focus:ring-gray-400/50 rounded-2xl"
                                      >
                    <div className={cardClass}>
                      {/* Image + overlay */}
                      <div className={imageWrapperClass}>
                        {hasImage ? (
                          <>
                            <img
                              src={`${config.REACT_APP_API_URL}${item.picture}`}
                              alt={item.name || "Produit"}
                              className={`${isGrid ? "h-full" : "h-[25rem] min-lg:h-[20rem]"} w-full  object-cover transition-transform duration-700 group-hover:scale-110`}
        loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                          </>
                        ) : (
    <div className={`${isGrid ? "h-full" : "h-[25rem] min-lg:h-[20rem]"} w-full bg-gray-50 flex flex-col items-center justify-center text-gray-400`}>
      <ImageOff className="w-20 h-20 mb-4 opacity-60" strokeWidth={1.4} />
      <p className="text-sm font-medium">Image non disponible</p>
    </div>
                        )}
                      </div>

                       {/* Contenu - Structure identique pour les deux vues */}
                <div className={`${isGrid ? "p-6 text-center" : "p-6 flex-1"} flex flex-col flex-1`}>
                  {/* Section 1: Nom (hauteur fixe) */}
                  <div className="h-16 text-center flex items-center">
                    <h3 className="text-xl font-medium text-gray-900 group-hover:text-black transition-colors line-clamp-2">
                      {item.name}
                    </h3>
                  </div>

                  {/* Section 2: Description (hauteur fixe) */}
                  <div className="mt-2 h-20">
                    <p className="text-sm text-gray-600 font-medium line-clamp-3">
                      {item.description?.substring(0, 100)}
                      {item.description?.length > 100 && "..."}
                    </p>
                  </div>

                      {/* Section 3: Code et Panier (alignés en bas) */}
                  <div className="mt-auto pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-600">
                        {item.code}
                      </span>
                      <button
                          onClick={() => handleAddToBasket(item._id)}
                          className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors shadow-sm"
                        >
                          <ShoppingCartIcon className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Section 4: Lien Voir le produit */}
                    <div className="mt-4 text-center">
                      <Link
                        to={`/single-product/${item._id}`}
                        className="inline-block text-sm font-medium text-gray-700 hover:text-black underline underline-offset-4"
                      >
                        Détails produit →
                      </Link>
                    </div>
                        </div>
                      </div>
                    </div>
                    </Link>
                  </motion.div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
    </>
  );
});

export default ByCategory;